import React, { useEffect, useMemo, useState } from 'react';
import { NavLink as RouterLink, useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import Hidden from '@mui/material/Hidden';
import Typography from '@mui/material/Typography';
import LoadingScreen from '../../../../components/LoadingScreen';
import useOrderFuelRedirect from '../../../../hooks/useOrderFuelRedirect';
import secureLocalStorage from 'react-secure-storage';
import HomeIcon from '../../../../assets/svgs/HomeIcon';
import MyOrders from '../../../../assets/svgs/MyOrders';
import MyAssets from '../../../../assets/svgs/MyAssets';
import MyPayments from '../../../../assets/svgs/MyPayments';
import DataAnalytics from '../../../../assets/svgs/DataAnalytics';
import { GET_COLOR } from "../../../../actions";
import { useDispatch, useSelector } from "react-redux";

const userId = secureLocalStorage.getItem('userId');

/**
 * Match a nav href to the current pathname.
 * `/payments` (Overview) and `/dashboard` are exact-only.
 * Other hrefs also match nested paths (e.g. `/payments/analytics/credit-utilisation`).
 */
const isNavHrefActive = (href, pathname) => {
    if (!href || !pathname) return false;
    if (pathname === href) return true;
    if (href === '/payments' || href === '/dashboard') return false;
    return pathname.startsWith(`${href}/`);
};

// A partner profile is a detail route, but it can be opened from Payments
// Overview, Analytics, or Activity. Keep the corresponding Payments submenu
// highlighted while the profile is open.
const getPaymentsNavigationPath = (location) => {
    if (!location?.pathname?.startsWith('/payments/partner/')) {
        return location?.pathname;
    }

    const originTrail = location.state?.originTrail || [];
    const sourcePath = [...originTrail]
        .reverse()
        .map((item) => item?.path)
        .find((path) => path?.startsWith('/payments/analytics') || path === '/payments/activity' || path === '/payments');

    return sourcePath || '/payments';
};

const useStyles = makeStyles(() => ({
    desktopDrawer: {
        width: 290,
        top: 64,
        height: 'calc(100% - 64px)',
        boxSizing: 'border-box'
    },
    root: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        boxSizing: 'border-box'
    },
    scrollArea: {
        flex: 1,
        minHeight: 0,
        width: '100%'
    },
    navList: {
        padding: 12
    },
    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 8,
        textDecoration: 'none',
        color: '#667085',
        minHeight: 36,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#F2F4F7'
        }
    },
    navItemActive: {
        backgroundColor: '#F4F5F8',
        color: '#0052FF',
        '&:hover': {
            backgroundColor: '#F4F5F8'
        }
    },
    navIcon: {
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 24px'
    },
    navLabel: {
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 600
    },
    chevron: {
        marginLeft: 'auto',
        width: 8,
        height: 8,
        borderRight: '2px solid currentColor',
        borderBottom: '2px solid currentColor',
        transform: 'rotate(45deg)',
        transition: 'transform 0.2s ease',
        marginBottom: 2
    },
    chevronOpen: {
        transform: 'rotate(225deg)',
        marginTop: 4,
        marginBottom: 0
    },
    submenuList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 24,
            left: 26,
            width: 2,
            backgroundColor: '#cbd5e1'
        }
    },
    submenuItemWrapper: {
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -12,
            left: 26,
            width: 20,
            height: 36,
            borderLeft: '2px solid #cbd5e1',
            borderBottom: '2px solid #cbd5e1',
            borderBottomLeftRadius: 10,
            pointerEvents: 'none'
        },
        '&:first-child::before': {
            top: 0,
            height: 24
        }
    },
    submenuItem: {
        display: 'block',
        padding: '10px 14px',
        marginLeft: 46,
        marginTop: 10,
        color: '#667085',
        fontWeight: 500,
        fontSize: 14,
        textDecoration: 'none',
        borderRadius: 8,
        '&:hover': {
            backgroundColor: '#F2F4F7'
        }
    },
    submenuItemActive: {
        backgroundColor: '#F4F5F8',
        color: '#0052FF',
        fontWeight: 600
    },
    bottomSection: {
        padding: 16
    },
    promoCard: {
        borderRadius: 12,
        padding: 16,
        background: 'linear-gradient(93deg, #005DFF -38.85%, #00297A 166.73%)',
        color: '#FFFFFF'
    },
    promoTitle: {
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 600,
        marginBottom: 6
    },
    promoSubtitle: {
        fontSize: 11,
        lineHeight: '14px',
        fontWeight: 500,
        opacity: 0.95,
        color: '#C7C7C7'
    },
    promoButton: {
        marginTop: 14,
        backgroundColor: '#FFFFFF',
        color: '#0B3B8F',
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        padding: '10px 12px',
        '&:hover': {
            backgroundColor: '#FFFFFF'
        }
    }
}));

const NavBar = ({
                    onMobileClose,
                    isPlaceOrderFlow,
                    openMobile,
                    play,
                    items: itemsProp,
                    promo,
                    drawerWidth = 290,
                    topOffset = 64
                }) => {
    const session = useSelector(state => state?.session);
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();
    const location = useLocation();
    const paymentsNavigationPath = getPaymentsNavigationPath(location);
    const { handleOrderFuel, isRedirecting } = useOrderFuelRedirect();

    const [openMenus, setOpenMenus] = useState({
        payments: location.pathname.includes('/payments')
    });

    const items = useMemo(() => {
        if (Array.isArray(itemsProp) && itemsProp.length) return itemsProp;

        const baseItems = [
            { id: 'home', title: 'Home', href: '/dashboard', icon: HomeIcon },
            { id: 'orders', title: 'Refuel', href: '/myOrders', icon: MyOrders },
            { id: 'assets', title: 'Usage', href: '/newasset', icon: MyAssets },
            {
                id: 'payments', 
                title: 'Payments', 
                icon: MyPayments,
                subItems: [
                    { id: 'pay-overview', title: 'Overview', href: '/payments' },
                    { id: 'pay-analytics', title: 'Analytics', href: '/payments/analytics' },
                    { id: 'pay-activity', title: 'Activity', href: '/payments/activity' }
                ]
            }
        ];

        if (['28066', '6417']?.includes(userId)) {
            baseItems.push({ id: 'analytics', title: 'Analytics', href: '/data-analytics', icon: DataAnalytics });
        }

        return baseItems;
    }, [itemsProp]);

    const resolvedPromo = useMemo(() => {
        if (promo === null) return null;
        return (
            promo || {
                title: 'Are you running out of fuel at your site?',
                subtitle: 'Fast, hassle-free fuel procurement.',
                ctaText: 'Order Fuel',
                to: '/place-order/select-asset'
            }
        );
    }, [promo]);

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose();
        }
        const isOnPayments = location.pathname.includes('/payments');
        if (isOnPayments && !openMenus.payments) {
            setOpenMenus(prev => ({ ...prev, payments: true }));
        } else if (!isOnPayments && openMenus.payments) {
            setOpenMenus(prev => ({ ...prev, payments: false }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handlePromoClick = () => {
        if (resolvedPromo?.to === '/place-order/select-asset' || resolvedPromo?.to === undefined) {
            handleOrderFuel();
        } else {
            history.push(resolvedPromo.to);
        }
    };

    const handleTrackAndDispatch = (item) => {
        play?.();
        dispatch({
            type: GET_COLOR,
            payload: item?.id
        });
    };

    const content = (
        <Box className={classes.root}>
            <Box className={classes.scrollArea}>
                <Hidden lgUp>
                    <Box p={2} display="flex" justifyContent="center" />
                </Hidden>
                <Box className={classes.navList}>
                    {items.map((item) => {
                        const Icon = item.icon;
                        const hasSubItems = item.subItems && item.subItems.length > 0;
                        
                        const isActiveRoute =
                            isNavHrefActive(item.href, location.pathname) ||
                            (hasSubItems &&
                                item.subItems.some((sub) =>
                                    isNavHrefActive(sub.href, paymentsNavigationPath)
                                ));

                        const activeColorCode = '#0052FF';
                        const inactiveColorCode = '#667085';
                        const isMenuOpen = openMenus[item.id];
                        
                        const isParentActiveVisual = isActiveRoute;

                        if (hasSubItems) {
                            return (
                                <Box key={item.id}>
                                    <Box
                                        className={`${classes.navItem}${isParentActiveVisual ? ` ${classes.navItemActive}` : ''}`}
                                        onClick={() => {
                                            // Check if we are already viewing a route inside this parent's submenu
                                            const isCurrentlyActiveSection = item.subItems.some((sub) =>
                                                isNavHrefActive(sub.href, paymentsNavigationPath)
                                            );
                                            
                                            if (!isCurrentlyActiveSection) {
                                                // If we're coming from outside (e.g. Home), route to the first sub-item and open menu
                                                history.push(item.subItems[0].href);
                                                setOpenMenus(prev => ({ ...prev, [item.id]: true }));
                                            } else {
                                                // If we're already inside (e.g. Analytics), just toggle the collapse open/close
                                                setOpenMenus(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                                            }
                                            
                                            handleTrackAndDispatch(item);
                                        }}
                                    >
                                        <Box className={classes.navIcon}>
                                            {Icon && <Icon size="20" colorCode={isParentActiveVisual ? activeColorCode : inactiveColorCode} />}
                                        </Box>
                                        <Typography className={classes.navLabel}>{item.title}</Typography>
                                        <Box className={`${classes.chevron} ${isMenuOpen ? classes.chevronOpen : ''}`} />
                                    </Box>

                                    <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
                                        <ul className={classes.submenuList}>
                                            {item.subItems.map(subItem => {
                                                const isSubActive = isNavHrefActive(
                                                    subItem.href,
                                                    paymentsNavigationPath
                                                );
                                                return (
                                                    <li key={subItem.id} className={classes.submenuItemWrapper}>
                                                        <Box
                                                            component={RouterLink}
                                                            to={subItem.href}
                                                            className={`${classes.submenuItem}${isSubActive ? ` ${classes.submenuItemActive}` : ''}`}
                                                            onClick={() => handleTrackAndDispatch(subItem)}
                                                        >
                                                            {subItem.title}
                                                        </Box>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </Collapse>
                                </Box>
                            );
                        }

                        return (
                            <Box
                                key={item.id}
                                component={RouterLink}
                                to={item.href}
                                className={`${classes.navItem}${isActiveRoute ? ` ${classes.navItemActive}` : ''}`}
                                exact
                                data-track-event={`${item?.title} Clicked`}
                                data-track-props={JSON.stringify({
                                    'Item Title': item?.title,
                                    'Item URL': item?.href
                                })}
                                onClickCapture={play}
                                onClick={() => {
                                    dispatch({
                                        type: GET_COLOR,
                                        payload: item?.id
                                    })
                                }}
                                sx={{ marginBottom: 1 }}
                            >
                                <Box className={classes.navIcon}>
                                    {Icon && <Icon size="20" colorCode={isActiveRoute ? activeColorCode : inactiveColorCode} />}
                                </Box>
                                <Typography className={classes.navLabel}>{item.title}</Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {(session?.detailsOfCustomer?.indent_creation && !isPlaceOrderFlow && resolvedPromo) && (
                <Box className={classes.bottomSection}>
                    <Box
                        className={classes.promoCard}
                        role="button"
                        tabIndex={0}
                        data-track-event="order_fuel_banner_clicked"
                        data-track-props={JSON.stringify({
                            banner_name: 'Order Fuel',
                            click_area: 'Full Banner'
                        })}
                        onClick={handlePromoClick}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handlePromoClick();
                            }
                        }}
                        sx={{cursor: 'pointer'}}
                    >
                        <Typography className={classes.promoTitle}>
                            {resolvedPromo?.title}
                        </Typography>
                        <Typography className={classes.promoSubtitle}>
                            {resolvedPromo?.subtitle}
                        </Typography>
                        <Button
                            className={classes.promoButton}
                            fullWidth
                            disabled={isRedirecting}
                        >
                            {resolvedPromo?.ctaText}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );

    if (isRedirecting) {
        return <LoadingScreen />
    }

    return (
        <>
            <Hidden lgDown>
                <Drawer
                    anchor="left"
                    classes={{ paper: classes.desktopDrawer }}
                    open
                    variant="persistent"
                    PaperProps={{
                        style: {
                            width: drawerWidth,
                            top: topOffset,
                            height: `calc(100% - ${topOffset}px)`
                        }
                    }}
                >
                    {content}
                </Drawer>
            </Hidden>
        </>
    );
};

NavBar.propTypes = {
    onMobileClose: PropTypes.func,
    openMobile: PropTypes.bool,
    isPlaceOrderFlow: PropTypes.bool,
    play: PropTypes.func,
    items: PropTypes.array,
    promo: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
    drawerWidth: PropTypes.number,
    topOffset: PropTypes.number
};

export default NavBar;

